<script setup>
/**
 * The screen the app opens on: the name, and every train this app knows going
 * through the same level crossing, one per pass, while the saved stations and
 * lines are read back. Shown once per launch, for as long as boot takes and
 * never less than a moment — a splash that flickers for 80 ms is worse than
 * none, and the first crossing takes half a second to arrive.
 *
 * ── AND EVERY CITY'S WORLD, ONE PER GROUP OF PASSES ──────────────────────────
 *
 * The parade is grouped by city, so the ten trains arrive as five blocks and
 * the WORLD changes five times a cycle rather than ten: Melbourne's two at the
 * level crossing, Sydney's three on the Harbour Bridge, Brisbane's two over the
 * Merivale bridge, Adelaide's two past the Oval, Canberra's one down
 * Northbourne Avenue. `PARADE` in `src/backdrops.js` is that order, derived
 * from the backdrop table's own livery lists rather than written out again
 * here, so a train can never be paraded on somebody else's scenery.
 *
 * ── HOW THE WORLD CHANGES: A CROSS-FADE, AT THE WRAP ─────────────────────────
 *
 * A cross-fade, not a cut, and it happens at a moment chosen rather than found.
 *
 * Every pass ends exactly where the scenery's 800-unit pattern wraps — that is
 * what a pass IS — so at a swap the outgoing world is at translate(0) and so is
 * the incoming one. Both scenes are therefore at the SAME position in their
 * patterns for the whole fade, and nothing slides sideways while it happens.
 * The outgoing scene is stacked over the incoming one and fades out; the
 * incoming one is at full opacity underneath the entire time, so the picture
 * never dims through the middle the way two half-faded copies would.
 *
 * The one number this needs is the phase. The splash's first scene opens at
 * `SPLASH_PHASE`, part-way in, so that the climb and the station happen while
 * it is up. Every scene mounted AFTER that opens at phase 0, because phase 0 is
 * the wrap, which is exactly where the world it is replacing had got to. Get
 * that wrong by even 0.18 of a loop and the two copies visibly slide past each
 * other for four hundred milliseconds.
 *
 * Changing the TRAIN is still free and still not a remount: within a city the
 * livery prop changes, Vue repaints the body, and the animations on `.scenery`
 * and `.lift` are left alone.
 *
 * ── WHY THE WHOLE SCENE RATHER THAN A CROSSING ON ITS OWN ────────────────────
 *
 * The brief offered two ways to build the parade: strip the scene back to a
 * still crossing and drive trains through it, or keep the scene exactly as it
 * is and change the train at the end of every lap. This is the second, and the
 * reason is that the first one has nothing moving in it.
 *
 * The scene's whole trick is that the world moves and the train stays put — it
 * is the view from the window. Freeze the world and the train stops looking
 * like it is travelling, which leaves a loading screen that does not read as
 * loading. The crossing is already the fixture here: it comes round every four
 * seconds, in the same place, whichever train is passing. So the crossing
 * stands still in the sense that matters — it is the one thing that does not
 * change — and ten different trains go through it.
 *
 * The swap itself is free. Changing the livery repaints the body and nothing
 * else: the animations live on `.scenery` and `.lift`, which Vue leaves alone,
 * so the world never jumps and the new train arrives already in stride.
 */
import { computed, onUnmounted, ref } from 'vue';
import TrainScene from './TrainScene.vue';
// Not from app-state: this is the FIRST thing painted, before the store has
// answered anything, and `city.js` and `liveries.js` are both import-free and
// read a single localStorage key between them. Pulling app-state in here would
// put the whole store, the push client and the Capacitor plugins in front of
// the splash.
import { city } from '../city.js';
import { FIRST_PASS_MS, LIVERIES, PASS_MS, SPLASH_PHASE } from '../liveries.js';
import { PARADE, WORLD_FADE_MS, backdropFor } from '../backdrops.js';
import { prefersReducedMotion } from '../core/a11y.js';

const props = defineProps({
  label: { type: String, default: 'Finding your trains…' },
  /**
   * One train instead of the parade.
   *
   * Settings' "The loading train" passes the current city's own livery: what
   * that row offers is a look at the thing the app shows YOU while it waits,
   * and a parade of nine other cities' trains would be a different promise.
   * The splash leaves it off and gets the parade.
   */
  livery: { type: String, default: '' },
  /**
   * Whose stamp to wear. The splash wears the current city's; Settings'
   * tour of the other cities passes each one in turn so the words above
   * the wordmark agree with the world below it.
   */
  cityId: { type: String, default: '' },
});

const stamp = computed(() => {
  const here = city(props.cityId || undefined);
  // Perth publishes nothing live, and the splash is the first thing a person
  // there sees: it says what the boards actually are.
  return `${here.name} trains · ${here.live === false ? 'timetable' : 'live'}`;
});

/**
 * Which train is passing.
 *
 * Melbourne first, always — it is the app's own city, and it is the train
 * every person who has ever opened this has seen on this screen. Then the rest
 * of the table in its own order, cycling, one per lap of the four-second loop.
 *
 * REDUCED MOTION STOPS THE PARADE, not just the animation. The scene already
 * holds still under the OS setting; a train that silently became a different
 * train every four seconds while nothing else moved would be the one piece of
 * motion left on a screen that had been asked for none.
 */
const still = prefersReducedMotion();
const at = ref(0);
const current = computed(() => props.livery || PARADE[at.value % PARADE.length]);
const name = computed(() => LIVERIES[current.value]?.name ?? '');

/**
 * The world under the train.
 *
 * `backdropFor` takes a LIVERY id here, not a city id, and that is the whole
 * reason it accepts both: the parade is a list of trains and nothing else, and
 * a second list saying which city each of them is in is a second list to keep
 * in step. Settings' single-train mode lands on the same line for free — it
 * passes its city's own livery, so the row shows that city's world.
 */
const world = computed(() => backdropFor(current.value));

/**
 * The phase the CURRENT scene was mounted at, and the world fading out over it.
 *
 * The first scene opens at `SPLASH_PHASE`; every one after it opens at 0,
 * because a swap happens at the wrap. `leaving` holds the outgoing world for
 * the length of the fade and is rendered above the new one — see the note at
 * the top.
 */
const phase = ref(SPLASH_PHASE);
const leaving = ref(null);
let clearing = null;

// The first swap comes when the first train has finished ITS lap, not a whole
// lap from now: the splash opens part-way into the loop, so the Melbourne
// train has already used 0.18 of its pass by the time this runs.
const parading = !props.livery && !still;

function pass() {
  const wasWorld = world.value;
  const wasLivery = current.value;
  at.value += 1;
  if (world.value === wasWorld) return;
  // Same tick as the change, so the incoming scene is mounted at the wrap
  // rather than re-timed a frame later.
  leaving.value = { world: wasWorld, livery: wasLivery };
  phase.value = 0;
  clearTimeout(clearing);
  clearing = setTimeout(() => { leaving.value = null; }, WORLD_FADE_MS);
}

let every = null;
const first = parading
  ? setTimeout(() => {
    pass();
    every = setInterval(pass, PASS_MS);
  }, FIRST_PASS_MS)
  : null;

onUnmounted(() => { clearTimeout(first); clearTimeout(clearing); clearInterval(every); });
</script>

<template>
  <div class="splash" role="status" aria-live="polite">
    <div class="stack">
      <p class="stamp">{{ stamp }}</p>
      <h1 class="wordmark">Late Again</h1>
      <!-- Opens on the approach, so the climb, the station and the way back
           down all happen inside the time this screen is up. The outgoing world
           is stacked OVER the incoming one and fades; both are at the pattern's
           wrap, so neither slides while it does. -->
      <div class="world" :style="{ '--world-fade': `${WORLD_FADE_MS}ms` }">
        <TrainScene :key="`in-${world}`" class="big" :phase="phase" :livery="current" :backdrop="world" />
        <TrainScene
          v-if="leaving"
          :key="`out-${leaving.world}`"
          class="big leaving"
          :phase="0"
          :livery="leaving.livery"
          :backdrop="leaving.world"
        />
      </div>
      <!-- What just went past. ARIA-HIDDEN on purpose: this is a live region,
           and a caption that changed every four seconds would have a screen
           reader announcing rolling stock over the top of the one sentence
           that says what the app is actually doing. -->
      <p class="train-name" aria-hidden="true">{{ name }}</p>
      <p class="label">{{ label }}</p>
    </div>
  </div>
</template>

<style scoped>
.splash {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  background: var(--la-carriage);
  color: var(--la-enamel);
  padding: env(safe-area-inset-top) 24px env(safe-area-inset-bottom);
}

.stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 420px;
}

.stamp, .label, .train-name {
  margin: 0;
  font-family: var(--la-font-mono);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--la-dim);
}

.wordmark {
  margin: 0 0 18px;
  font-family: var(--la-font-display);
  font-stretch: 125%;
  font-size: 40px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1;
}

.big { width: 100%; }

/* The two scenes during a world change. The incoming one holds the box open;
   the outgoing one is taken out of flow, laid exactly over it, and faded. */
.world { position: relative; width: 100%; }
.leaving {
  position: absolute;
  inset: 0;
  /* `--world-fade` is bound from WORLD_FADE_MS, because the timer that removes
     this element and the animation that hides it have to be the same number: a
     fade longer than the timer ends in a snap, a shorter one ends in a flash of
     the old world coming back. */
  animation: world-out var(--world-fade) ease-in forwards;
}

@keyframes world-out { from { opacity: 1; } to { opacity: 0; } }

/* The parade does not run under reduced motion, so nothing here ever fades —
   but if the setting is turned on mid-fade, it ends now rather than easing. */
@media (prefers-reduced-motion: reduce) {
  .leaving { animation: none; opacity: 0; }
}

/* The train's own name, right under it, in the same mono caps as everything
   else on this screen — but dimmer still, because it is a caption on a picture
   and not the sentence the person is waiting on. */
.train-name {
  margin-top: 10px;
  opacity: 0.62;
}

.label { margin-top: 10px; }
</style>
